/* eslint-disable jsx-a11y/anchor-is-valid */
import cx from "classnames";
import { useEffect, useState } from "react";
import {
  handleConnectPaymentPreferenceProps,
  handleDisconnectPaymentPreferenceProps,
} from ".";
import { useToast } from "../../../../../hooks/use-toast.hook";
import { PaymentPreference } from "../../../../../types/payment-preference/payment-preference.type";
import { Button, TextInput } from "../../../../../_components";
import { NewActionModal } from "../../../../../_components/action2-modal.component";
import I18 from "../../../../../_components/atoms/i18";
import CopyTextField from "../../../../../_components/molecule/copy-text-field.component";
import Label from "../../../../../_components/typography/label.typography";
import { getCommunityAddress } from "../../../../../_config/helper.config";
import { Fold } from "../../../../../_utils/extensions/typescript-utils";
import Validator from "../../../../../_utils/validator";

type Status = "idle" | "loading" | "success" | "error";
interface Props {
  community: any;
  paymentPreference: PaymentPreference | null | undefined;
  status: Status;
  handleDisconnectPaymentPreference: ({
    onSuccess,
    onFailed,
  }: handleDisconnectPaymentPreferenceProps) => void;
  handleConnectPaymentPreference: ({
    payload,
    onSuccess,
    onFailed,
  }: handleConnectPaymentPreferenceProps) => void;
}
/**
 * Component to display razorpay account details
 */
export default function LinkRazorPayView({
  status,
  community,
  paymentPreference,
  handleConnectPaymentPreference,
  handleDisconnectPaymentPreference,
}: Props) {
  const razorpayAccount = paymentPreference?.razorpay;
  const integrationStatus = paymentPreference?.integrationStatus;
  const [razorpayId, setRazorpayId] = useState("");
  const [razorpaySecret, setRazorpaySecret] = useState("");
  const [razorpayWebhookSecret, setRazorpayWebhookSecret] = useState("");
  const [activeDiscount, setActiveDiscount] = useState(false);

  const isRazorPayAccount =
    Validator.hasValue(razorpayAccount?.razorpayId) &&
    paymentPreference?.paymentMethod === "razorpay";

  const toast = useToast();

  //  Initialize razorpay account details
  useEffect(() => {
    if (!razorpayAccount) {
      return;
    }
    if (razorpayAccount.razorpayId) {
      setRazorpayId(razorpayAccount.razorpayId);
      setRazorpaySecret("**************************");
      setRazorpayWebhookSecret("**************************");
    }
  }, [razorpayAccount]);

  function onFormSubmit() {
    if (paymentPreference?.razorpay?.razorpayId) {
      setActiveDiscount(true);
    } else {
      const payload = {
        paymentMethod: "razorpay",
        razorpayId,
        razorpaySecret,
        razorpayWebhookSecret,
      };
      handleConnectPaymentPreference({
        payload: payload,
        onSuccess: () => {
          toast.addToast(
            "Razorpay account has been successfully linked",
            "",
            "success"
          );
        },
        onFailed: () => {},
      });
    }
  }

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onFormSubmit();
        }}
        className="theme-bg-default py-3 px-4  my-4 rounded">
        <div className="flex flex-col sm:flex-row gap-2 justify-between font-semibold pb-4 sm:pb-0">
          <I18>Link your razorpay account</I18>
          {/* {isRazorPayAccount && (
            <RenderStatus integrationStatus={"completed"} />
          )} */}
          {["not-set", undefined, null].includes(
            paymentPreference?.paymentMethod
          ) &&
            Validator.hasValue(integrationStatus) && (
              <RenderStatus integrationStatus={integrationStatus} />
            )}
        </div>
        <div className="flex flex-col gap-2 py-2 mt-2">
          <div className="flex flex-col gap-1">
            <Label variant="t1">Razorpay Id</Label>
            <TextInput
              required
              readOnly={razorpayAccount?.razorpayId ? true : false}
              defaultValue={razorpayId}
              noMargin
              className={cx("theme-bg-surface", {
                "cursor-not-allowed": razorpayAccount?.razorpayId
                  ? true
                  : false,
              })}
              placeholder="Enter Razorpay id"
              onChange={(e) => setRazorpayId(e)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label variant="t1">Razorpay secret</Label>
            <TextInput
              type="text"
              noMargin
              required
              readOnly={razorpayAccount?.razorpayId ? true : false}
              className="theme-bg-surface "
              defaultValue={razorpaySecret}
              placeholder="Enter razorpay secret"
              onChange={(e) => setRazorpaySecret(e)}
            />
          </div>
          <div className="flex flex-col gap-1 my-2">
            <div className="flex items-center gap-2">
              <Label variant="t1">Razorpay webhook secret</Label>
            </div>
            <TextInput
              type="text"
              noMargin
              required
              readOnly={razorpayAccount?.razorpayId ? true : false}
              className="theme-bg-surface "
              defaultValue={razorpayWebhookSecret}
              placeholder="Enter razorpay webhook secret"
              onChange={(e) => setRazorpayWebhookSecret(e)}
            />
          </div>

          <div className=" flex flex-col gap-1 my-2">
            <Label variant="t1">Razorpay webhook url</Label>
            <CopyTextField
              text={`http://${getCommunityAddress(
                community
              )}/webhooks/razorpay/${community.id}`}
              className="theme-text-subtitle-1` text-xs overflow-x-auto border theme-border-default p-1 rounded theme-bg-surface"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center place-content-between my-4 w-full">
          <Fold
            value={razorpayAccount?.razorpayId}
            ifPresent={() => (
              <Button
                label="Disconnect"
                isLoading={status === "loading"}
                btnColor="red"
              />
            )}
            ifAbsent={() => (
              <Button label="Connect" isLoading={status === "loading"} />
            )}
          />

          <a
            title="How to link Razorpay account via API and webhook"
            referrerPolicy="origin"
            rel="noopener noreferrer"
            target="_blank"
            href="https://help-centre.pensil.in/p/how-to-link-razorpay-account-via-api-and-webhook">
            How to find api key and secret key in Razorpay
          </a>
        </div>
      </form>
      <NewActionModal
        title={"Are you sure you want to disconnect Razorpay account?"}
        active={activeDiscount}
        children={
          <div className="flex flex-col gap-3 items-center place-content-center mx-4 mb-4 mt-3">
            <Label variant="t1" className="theme-text-danger">
              Disconnect Razorpay
            </Label>
            <div className="flex items-center place-content-center p-4 theme-bg-default border theme-border-default rounded">
              <Label size="caption">
                Note: All your membership will be deactivated.
              </Label>
            </div>
          </div>
        }
        setActive={setActiveDiscount}
        labelCancel="Cancel"
        labelSubmit="Disconnect"
        btnColor="red"
        onCancelSubmit={() => {
          setActiveDiscount(false);
        }}
        onSubmit={() => {
          setActiveDiscount(false);
          handleDisconnectPaymentPreference({
            onSuccess: () => {
              toast.addToast("Razorpay Account Disconnected", "", "success");
              setRazorpayId("");
              setRazorpaySecret("");
              setRazorpayWebhookSecret("");
            },
            onFailed: () => {},
          });
        }}
      />
    </>
  );
}
export function RenderStatus({
  integrationStatus,
}: {
  integrationStatus?: "pending" | "completed" | "not-set";
}) {
  if (
    !integrationStatus ||
    !["pending", "completed", "not-set"].includes(integrationStatus)
  ) {
    return <></>;
  }
  const Status = {
    "not-set": "Not Connected",
    pending: "Pending",
    completed: "Connected",
  };
  return (
    <div className="flex flex-row gap-2">
      <I18>Status</I18>:
      <div
        className={cx("flex-row flex items-center gap-1", {
          "theme-text-danger": integrationStatus === "pending",
          "theme-text-warning": integrationStatus === "not-set",
          "theme-text-success": integrationStatus === "completed",
        })}>
        <div
          className={cx("h-2 w-2 rounded-full", {
            "theme-bg-danger": integrationStatus === "pending",
            "theme-bg-warning": integrationStatus === "not-set",
            "theme-bg-success": integrationStatus === "completed",
          })}
        />
        <Label className="first-letter:uppercase">
          {Status[integrationStatus]}
        </Label>
      </div>
    </div>
  );
}
